import { useNavigate } from "@tanstack/react-router";
import { Card, Skeleton, Typography } from "antd";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { DepartmentBreakdown } from "../types";
import { davomatDarajasi } from "../utils/davomatDarajasi";
import { BarList, type BarListItem } from "./BarList";
import { MAQSAD_FOIZ } from "./palette";

const { Text } = Typography;

export interface DepartmentAttendanceCardProps {
	/**
	 * ⚠️ **PROP, o'z hook'i EMAS.** Kesim `/attendance/dashboard`
	 * javobining ICHIDA keladi (`departments`), alohida marshrut YO'Q:
	 * karta panelda DOIM ekranda va shartli parametr faqat ikkinchi kesh
	 * kaliti hamda ikkinchi so'rov keltirardi.
	 */
	departments: DepartmentBreakdown[];
	loading?: boolean;
}

/**
 * Bo'lim yorlig'i.
 *
 * ⚠️⚠️ **IKKI BOSHQA holat, bitta yorliqqa QO'SHILMAYDI.**
 * `departmentId === null` — xodimning bo'limi biriktirilmagan
 * («Bo'limsiz»); `departmentId !== null && departmentName === null` —
 * bo'lim topilmadi («Nomi topilmadi»). `departmentName ?? "Bo'limsiz"`
 * yozilsa ular bitta qatorga qo'shilib, sanoqlar noto'g'ri yig'ilardi.
 *
 * ⚠️ P7 i18n: `t` PARAMETR — `modules/audit/utils/actions.ts`ning
 * `amalNomi(action, t)` naqshi.
 */
function yorliq(qator: DepartmentBreakdown, t: TFunction): string {
	if (qator.departmentName !== null) {
		return qator.departmentName;
	}

	return qator.departmentId === null ? t("departments.unassigned") : t("departments.unknown");
}

/**
 * Bo'limlar kesimida davomat.
 *
 * ⚠️ **ROLLUP YO'Q — LEAF kesimi.** `departmentId` xodimning O'Z
 * bo'limi. `departments.path` bo'yicha ota-onaga yig'ish ikkinchi ma'no
 * bo'lardi (bir xodim ikki qatorda). B `parentId` ni beradi, lekin
 * v1 da u ISHLATILMAYDI.
 *
 * ⚠️ **«Bo'limsiz» qatori ATAYLAB ko'rsatiladi.**
 * `employees.department_id` NULLABLE; NULL guruhi tashlab yuborilsa,
 * kartaning foizlari asosiy sanoqlardan BOSHQA kogortadan hisoblanib,
 * jami mos kelmasdi.
 *
 * ⚠️ **Bo'lim nomi bosiladi — `/departments/$id` ga TIPLANGAN navigatsiya**
 * (`useNavigate` + `to`/`params`, `DepartmentsExplorerTable.onRow` bilan
 * bir xil naqsh), xom `navigate({ href })` EMAS: tiplangan chaqiruv
 * `departmentDetailRoute` o'chirilsa yoki nomi o'zgarsa typecheck'da
 * ushlanadi. `departmentId === null` («Bo'limsiz») qatorida `onClick`
 * BERILMAYDI — bu kogortaning o'z profili yo'q.
 *

 * ⚠️ **`plannedShifts = 0` bo'lgan bo'lim «—» oladi va tartibda
 * OXIRIDA turadi**, «0%» bilan «eng yomon bo'lim» qilib
 * ko'rsatilmaydi.
 */
export function DepartmentAttendanceCard({
	departments,
	loading = false,
}: DepartmentAttendanceCardProps) {
	const { t } = useTranslation("dashboard");
	const navigate = useNavigate();
	const qatorlar: BarListItem[] = departments
		.map((qator) => ({
			id: qator.departmentId ?? "bolimsiz",
			name: yorliq(qator, t),
			foiz: davomatDarajasi(qator.boshlangan, qator.plannedShifts),
			onClick: qator.departmentId
				? () => navigate({ to: "/departments/$id", params: { id: qator.departmentId as string } })
				: undefined,
		}))
		// ⚠️ `null` lar OXIRIDA: ular «yomon» emas, «o'lchanmagan».
		.sort((a, b) => {
			if (a.foiz === null) {
				return b.foiz === null ? 0 : 1;
			}

			if (b.foiz === null) {
				return -1;
			}

			return b.foiz - a.foiz;
		});

	return (
		<Card
			title={t("departments.title")}
			extra={
				<Text type="secondary" className="text-xs">
					{t("departments.extra")}
				</Text>
			}
		>
			{loading ? (
				<Skeleton active paragraph={{ rows: 4 }} title={false} />
			) : qatorlar.length === 0 ? (
				<Text type="secondary" className="text-xs">
					{t("departments.empty")}
				</Text>
			) : (
				<>
					<BarList items={qatorlar} target={MAQSAD_FOIZ} />

					<Text type="secondary" className="mt-3 block text-xs">
						{t("departments.footer", { value: MAQSAD_FOIZ })}
					</Text>
				</>
			)}
		</Card>
	);
}
